import { useCallback, useEffect, useState } from 'react';
import { LogOut, Sun, Moon, MonitorSmartphone, WifiOff } from 'lucide-react';
import { Outlet, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { NfcAgentProvider, normalizeLocale, useAuthStore, useLogout, useMqttConnection, useTranslation } from '@bms/common';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { AppSidebar } from '@/components/sidebar/AppSidebar';
import { NfcAgentStatusBadge } from '@/components/NfcAgentStatusBadge';
import { CardTapHandler } from '@/components/CardTapHandler';
import { DeskPalette } from '@/components/search/DeskPalette';
import { DeskSearchField } from '@/components/search/DeskSearchField';
import { CheckInGuestButton } from '@/components/desk/CheckInGuestButton';
import { useTheme } from '@/providers/ThemeProvider';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePullToRefresh } from '@/hooks/use-pull-to-refresh';
import { PullIndicator } from '@/components/common/PullIndicator';
import { PageChromeProvider, usePageChrome } from '@/components/common/PageChrome';
import { PageHeading, useRouteCrumbs } from '@/components/common/PageHeading';
import { InstallPrompt } from '@/components/InstallPrompt';
import { useHousekeepingAccess } from '@/lib/housekeepingMode';
import { cn } from '@/lib/utils';
import { useLivePausedSince } from './useLivePausedSince';

// ============================================================================
// MainLayout — shadcn sidebar-07 pattern (variant="sidebar")
// Standard sidebar with border-right + main content area
//
// The shell owns three things pages used to fight it over: the page's padding
// (a page that needs the full width asks for it through PageChrome instead of
// applying negative margins), where the user is (a route-driven breadcrumb,
// not a per-page header), and whether the card entrance animation may run
// (once per session, not on every re-render).
// ============================================================================

/**
 * How long the staggered card entrance needs before it can be switched off:
 * the longest stagger delay (600 ms, see DashboardPage) plus the animation
 * itself (--duration-slow, 300 ms) plus a frame of margin.
 */
const CARD_ENTER_SETTLE_MS = 1000;
const CARD_ENTER_SESSION_KEY = 'bms-cards-entered';

function readCardsEntered(): boolean {
  try {
    return sessionStorage.getItem(CARD_ENTER_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/** "Live updates paused since 10:42 · retrying · showing last known values" (audit P7). */
function LivePausedStrip({ since }: { since: Date | null }) {
  const { t, i18n } = useTranslation();
  if (!since) return null;
  const time = new Intl.DateTimeFormat(normalizeLocale(i18n.language), {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(since);
  return (
    <div
      role="status"
      className="flex shrink-0 items-center gap-2 border-b border-warning/30 bg-warning/10 px-4 py-1.5 text-caption text-warning-text"
    >
      <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="min-w-0">{t('shell.livePaused', { time })}</span>
    </div>
  );
}

/** Theme toggle: icon and label read the *preference*, not the painted mode. */
const THEME_ICON = { light: Sun, dark: Moon, system: MonitorSmartphone } as const;

function MainLayoutContent() {
  const { mode, preference, toggleMode, branding } = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const location = useLocation();
  const crumbs = useRouteCrumbs(location.pathname);
  const currentSiteCode = useAuthStore((state) => state.currentSiteCode);
  const user = useAuthStore((state) => state.user);
  const authLogout = useAuthStore((state) => state.logout);
  const logout = useLogout();
  const { isAttendantMode } = useHousekeepingAccess();

  // Initialize MQTT WebSocket connection for real-time channel state updates
  const mqttConnection = useMqttConnection({ siteCode: currentSiteCode ?? undefined });

  // While the socket is down the board shows last known values; say so under
  // the header until it returns, instead of a toast that fades (audit UX-24).
  const livePausedSince = useLivePausedSince(mqttConnection.state);

  // Browser-level offline/online toast. It fires off actual network
  // transitions (navigator online/offline) rather than MQTT socket state, and
  // catches loss of connectivity faster than the MQTT reconnect loop does.
  useEffect(() => {
    const handleOffline = () =>
      toast.error(t('shell.offline'), {
        id: 'network-status',
      });
    const handleOnline = () => toast.success(t('shell.backOnline'), { id: 'network-status' });
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [t]);

  // The card entrance is a first-impression animation, not a state change, so
  // it runs once per session. Once the first board has settled the shell
  // stamps the scroll container and index.css switches the keyframes off, so
  // filtering, re-sorting or navigating back never replays the stagger.
  const [cardsEntered, setCardsEntered] = useState(readCardsEntered);
  useEffect(() => {
    if (cardsEntered) return;
    const timer = window.setTimeout(() => {
      try {
        sessionStorage.setItem(CARD_ENTER_SESSION_KEY, '1');
      } catch {
        /* private mode — the entrance simply runs again on the next load */
      }
      setCardsEntered(true);
    }, CARD_ENTER_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [cardsEntered]);

  const handleRefresh = useCallback(async () => {
    await queryClient.invalidateQueries();
  }, [queryClient]);

  const { ref: scrollRef, isRefreshing, pullDistance } = usePullToRefresh<HTMLDivElement>({
    onRefresh: handleRefresh,
    enabled: isMobile,
  });

  const ThemeIcon = THEME_ICON[preference];
  const themeLabel = t(`shell.themeToggle.${preference}`);

  if (isAttendantMode) {
    const handleLogout = () => {
      logout.mutate();
      authLogout();
      window.location.href = '/login';
    };

    return (
      <PageChromeProvider>
        <div className="flex h-svh min-h-0 flex-col bg-background">
          {/* Slim banner above the header, clear of the bottom-right toasts */}
          <InstallPrompt />
          <header className="flex h-12 shrink-0 items-center gap-3 border-b px-3">
            <img
              src={branding.logoUrl || (mode === 'dark' ? '/logo-dark.svg' : '/logo-light.svg')}
              alt={t('shell.brandAlt')}
              className="h-7 w-7 object-contain"
            />
            <span className="min-w-0 flex-1 truncate text-ui font-medium">
              {user?.displayName || user?.username || t('shell.housekeepingFallbackName')}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground"
              onClick={toggleMode}
              aria-label={themeLabel}
            >
              <ThemeIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground"
              onClick={handleLogout}
              aria-label={t('nav.logout')}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </header>
          <LivePausedStrip since={livePausedSince} />
          <MainScroll
            scrollRef={scrollRef}
            isRefreshing={isRefreshing}
            pullDistance={pullDistance}
            cardsEntered={cardsEntered}
            padded={false}
            hasPageHeading
            pageHeading={crumbs}
          />
        </div>
      </PageChromeProvider>
    );
  }

  return (
    <PageChromeProvider>
      <SidebarProvider>
        <CardTapHandler />
        {/* One search for the desk (P5): Ctrl+K, the header field, a card tap. */}
        <DeskPalette />
        {/* Skip-to-content link for keyboard users */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-ui focus:font-medium"
        >
          {t('shell.skipToContent')}
        </a>
        <AppSidebar />
        <SidebarInset className="min-w-0">
          {/* Slim banner above the header, clear of the bottom-right toasts */}
          <InstallPrompt />

          {/* Header — trigger, then where the user is, then global controls */}
          <header className="flex h-14 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
            <div className="flex shrink-0 items-center gap-2 pl-4">
              <SidebarTrigger className="-ml-1 sm:size-7 size-9" />
              <Separator orientation="vertical" className="data-[orientation=vertical]:h-4" />
            </div>

            <div className="min-w-0 flex-1 pl-1">
              <PageHeading crumbs={crumbs} />
            </div>

            <div className="flex shrink-0 items-center gap-2 pr-4">
              {/* F1: the desk's first job, before search, on every page. */}
              <CheckInGuestButton />
              <DeskSearchField />
              <NfcAgentStatusBadge />
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground"
                onClick={toggleMode}
                aria-label={themeLabel}
              >
                <ThemeIcon className="h-4 w-4" />
              </Button>
            </div>
          </header>
          <LivePausedStrip since={livePausedSince} />

          <MainScroll
            scrollRef={scrollRef}
            isRefreshing={isRefreshing}
            pullDistance={pullDistance}
            cardsEntered={cardsEntered}
            padded
            hasPageHeading
          />
        </SidebarInset>
      </SidebarProvider>
    </PageChromeProvider>
  );
}

export default function MainLayout() {
  return (
    <NfcAgentProvider>
      <MainLayoutContent />
    </NfcAgentProvider>
  );
}

/**
 * The one scroll container. It reads PageChrome, so it has to sit inside the
 * provider — hence a component rather than JSX inlined into both branches.
 */
function MainScroll({
  scrollRef,
  isRefreshing,
  pullDistance,
  cardsEntered,
  padded,
  hasPageHeading,
  pageHeading,
}: {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  isRefreshing: boolean;
  pullDistance: number;
  cardsEntered: boolean;
  padded: boolean;
  /** Whether a `PageHeading` renders in the shell or this scroll area, so the landmark can borrow its text as an accessible name. */
  hasPageHeading: boolean;
  /** Mobile shells place the complete heading below their fixed bar. */
  pageHeading?: Parameters<typeof PageHeading>[0]['crumbs'];
}) {
  const { fullBleed } = usePageChrome();

  return (
    <main
      id="main-content"
      ref={scrollRef}
      aria-labelledby={hasPageHeading ? 'page-heading-current' : undefined}
      data-cards-entered={cardsEntered ? 'true' : undefined}
      className={cn(
        'flex min-w-0 flex-1 flex-col overflow-y-auto',
        // A full-bleed page brings its own padding and its own sticky header;
        // the shell contributes nothing to it but the home-indicator inset.
        fullBleed || !padded
          ? 'pb-safe'
          : 'gap-2 p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))] sm:p-4 sm:pb-[calc(1rem+env(safe-area-inset-bottom,0px))]',
      )}
    >
      <PullIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      {pageHeading && (
        <div className="shrink-0 border-b px-4 py-3">
          <PageHeading crumbs={pageHeading} />
        </div>
      )}
      <Outlet />
    </main>
  );
}
